import solutionPlanningVideo from '@/assets/cases/freight-invoicing/0.mov';
import ratebookOverviewVideo from '@/assets/cases/freight-invoicing/2.mov';
import ratebookDimensionsVideo from '@/assets/cases/freight-invoicing/2a.mov';
import ratebookConditionsVideo from '@/assets/cases/freight-invoicing/2b.mov';
import ratebookAccessorialVideo from '@/assets/cases/freight-invoicing/2c.mov';
import ratebookBulkUpdatesVideo from '@/assets/cases/freight-invoicing/2d.mov';
import lineItemsIntelligenceVideo from '@/assets/cases/freight-invoicing/3.mov';
import launchpadVideoMov from '@/assets/cases/freight-invoicing/5.mov';
import launchpadBlur from '@/assets/cases/freight-invoicing/5.blur.webp';
import freightCoverImage from '@/assets/work-carousel/freight-invoicing.png';
import creditRebillUx from '@/assets/cases/freight-invoicing/6.jpg';
import {CaseStudy} from './types';

const ratebookOverviewVideoAsset = ratebookOverviewVideo;
const ratebookDimensionsVideoAsset = ratebookDimensionsVideo;
const ratebookConditionsVideoAsset = ratebookConditionsVideo;
const ratebookAccessorialVideoAsset = ratebookAccessorialVideo;
const ratebookBulkUpdatesVideoAsset = ratebookBulkUpdatesVideo;
const lineItemsIntelligenceVideoAsset = lineItemsIntelligenceVideo;
const launchpadVideo = launchpadVideoMov;

const freightInvoicing: CaseStudy = {
    // ============================================
    // FIXED METADATA
    // ============================================
    id: 'freight-invoicing-platform',
    slug: 'freight-invoicing-calculation-engine',
    title: 'Freight Invoicing',
    client: 'Fortune 100/500 Refined Products Company',
    industry: 'Energy & Logistics',
    projectType: 'Zero-to-One Platform Development',
    timeline: '6 weeks',
    technologies: [
        'React',
        'TypeScript',
        'Ant Design',
        'Ag Grid',
        'REST APIs',
        'PDF Generation'
    ],

    shortDescription:
        'Engineered the freight pricing engine that respects every customer contract so a refined products leader could enter the carrier market.',
    excerpt:
        'Built a 40-page freight playbook, dimensional ratebooks, transparent line-item intelligence, credit/rebill guardrails, and launchpad monitoring—170 features that make carrier invoicing reliable.',
    heroImageUrl: launchpadVideo,
    heroGalleryImage: freightCoverImage,
    featured: true,
    workHighlight: {
        id: 'freight-invoicing',
        mediaType: 'video',
        media: launchpadVideoMov,
        mediaSources: [
            {src: launchpadVideoMov, type: 'video/quicktime'}
        ],
        placeholder: launchpadBlur
    },

    // ============================================
    // HERO SECTION DATA
    // ============================================
    heroStatement:
        'Turned fragmented freight rules into a carrier-ready invoicing platform—documentation, rate intelligence, adjustments, and monitoring in one system.',
    heroMetrics: [
        {label: 'Features delivered', value: '170'},
        {label: 'Freight scenarios covered', value: '95%'},
        {label: 'Carrier launch timeline', value: '6 weeks'}
    ],
    heroNarrative:
        'The client dominated refined products distribution but invoicing freight required spreadsheets, email approvals, and subject-matter experts. Their enterprise SaaS product had all the data, yet they could not serve carriers until freight pricing matched every customer contract and proved it on paper.\n\nWe partnered with the client’s product lead and lead developer, produced the documentation the business never had, codified dimensional ratebooks, exposed every calculation in-line, wrapped adjustments in a credit/rebill experience, and shipped monitoring so finance sees exceptions before revenue slips.',

    // ============================================
    // SCOPE OF WORK (4 Service Areas)
    // ============================================
    scopeOfWork: [
        {
            icon: 'psychology',
            title: 'Design Thinking',
            number: '01',
            description:
                'Facilitated supply and dispatch workshops to map end-to-end freight operations and surface carrier market blockers.'
        },
        {
            icon: 'library_books',
            title: 'Operational Documentation',
            number: '02',
            description:
                'Produced a 40-page object-driven blueprint capturing contracts, actors, triggers, and dependencies for every freight scenario.'
        },
        {
            icon: 'design_services',
            title: 'UX Prototyping',
            number: '03',
            description:
                'Prototyped ratebooks, line-item intelligence, credit/rebill, and monitoring interfaces to validate workflows with stakeholders.'
        },
        {
            icon: 'code',
            title: 'Frontend Engineering',
            number: '04',
            description:
                'Implemented React/TypeScript applications that automate pricing rules, render live invoices, and surface freight health in real time.'
        }
    ],

    // ============================================
    // CASE DETAILS (Collapsible Card)
    // ============================================
    caseDetails: {
        task:
            'Standardize freight pricing and invoicing so an enterprise refined products company could launch its carrier offering without manual reconciliation.',
        solution:
            'Documented the freight domain, engineered dimensional ratebooks with accessorial and surcharge engines, exposed line-item intelligence with override audit trails, and built credit/rebill and monitoring modules on top of the same object model.',
        result:
            'Freight is calculated automatically across 95% of scenarios, with manual fees handled inside the same UI. Dispatch sees pending freight before approving supply plans, finance resolves overrides with full history, and leadership monitors 100+ customers from a single launchpad.',
        stack: ['React', 'TypeScript', 'Ant Design', 'Ag Grid', 'REST APIs', 'PDF Generation']
    },

    // ============================================
    // DYNAMIC CONTENT (Visual Storytelling)
    // ============================================
    content: [
        // 1. BREAKING DOWN THE PROBLEM
        {
            type: 'heading2',
            content: '1. Breaking Down the Problem',
            id: 'problem-foundation',
            description: '150+ freight scenarios demanded an engine, not spreadsheets.'
        },
        {
            type: 'paragraph',
            content:
                'The client’s product lead and lead developer knew carriers needed invoice confidence. Every carrier contract read differently—mileage ladders, per-truck minimums, lane overrides, automatic and manual fees. We sat with them to map 150+ scenarios the engine had to support. That collaboration produced the densest spec we’ve ever written: object models, state diagrams, and interface contracts that future-proofed the freight engine.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Problem We Faced', 'How We Unlocked It'],
                rows: [
                    [
                        'Tribal knowledge about freight contracts lived with the product lead and a handful of SMEs.',
                        'Co-authored a 40-page object blueprint with the product lead and developer that captured every entity, state, and dependency the engine needed.'
                    ],
                    [
                        '150+ scenarios had no shared vocabulary—development stalled in translation.',
                        'Created a taxonomy for rate types, triggers, and adjustments so product and engineering spoke the same language.'
                    ],
                    [
                        'Invoice flows branched endlessly; nobody agreed when freight was pending, estimated, or final.',
                        'Mermaid diagrams visualized order → invoice → reconciliation so edge cases were explicit before coding.'
                    ],
                    [
                        'Future features risked breaking the engine because requirements weren’t formalized.',
                        'Documented interface contracts the developer could extend, keeping the engine adaptable instead of brittle.'
                    ]
                ]
            }
        },
        {
            type: 'mermaidGallery',
            content: {
                heading: 'Solution Planning / Architecture',
                caption: 'Video walkthrough and the mermaid flows we used to codify the freight engine.',
                rows: [
                    {
                        layout: 'full',
                        diagrams: [
                            {
                                kind: 'video',
                                src: solutionPlanningVideo,
                                title: 'Solution Planning Walkthrough',
                                description: 'Workshop playback aligning ratebooks, adjustments, and monitoring into one platform.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        diagrams: [
                            {
                                code: `flowchart TD
    A[Start: Rate Setup] --> B[Base Rate Configuration]
    B --> C[Accessorial Setup]
    C --> D[Point-to-Point Definition]
    D --> E[Surcharge Configuration]
    E --> F[Better Of Rules]
    F --> G[Validation]
    G -->|Failed| B
    G -->|Passed| H[Rates Active]`,
                                title: 'Rate Setup Process',
                                description: 'Configuration flow for rate management'
                            },
                            {
                                code: `flowchart TD
    A[Order Creation] --> B[Load Analysis]
    B --> C[State Check]
    C -->|Multi-State| D[State Split]
    C -->|Single State| E[Customer Check]
    D --> E
    E -->|Multi-Customer| F[Customer Split]
    E -->|Single Customer| G[Product Check]
    F --> G
    G -->|Multi-Product| H[Product Split]
    G -->|Single Product| I[Validation]
    H --> I
    I -->|Failed| B
    I -->|Passed| J[Segments Created]`,
                                title: 'Order Segmentation',
                                description: 'Logic for splitting orders into segments'
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        diagrams: [
                            {
                                code: `graph TB
    subgraph Core["Core Process"]
        A[Freight Segment] --> B{Rate Type}
        B -->|P2P| C[P2P Rate]
        B -->|Standard| D[Band Rate]
        B -->|Not Found| ER[Create Error Transaction]
        C --> E[Base Transaction]
        D --> E
        E --> F[Auto Accessorials]
        F --> G[Segment Subtotal]
        G --> H[Surcharge]
        H --> I[Secondary Surcharge]
        I --> J[Tax]
        J --> K[Final Validation]
        K --> L[Attach to Invoice]
        L --> S[Set Status]
    end

    subgraph OrderEvents["Order Events"]
        O1[Order Created]
        O2[Order Completed]
        O3[Movement Changed]
    end

    subgraph CustomerConfig["Customer Configuration"]
        CC{Invoicing Active?}
    end

    subgraph ExternalEvents["External Events"]
        E1[Manual Accessorial] --> L
        E2[Special Pay Request] --> L
    end

    subgraph RecalcSegment["Segment Recalculation"]
        RFS[Recalc Freight Segment]
    end

    subgraph RecalcTrans["Transaction Recalculation"]
        RT[Recalc Transactions]
    end

    O1 & O2 & O3 --> CC
    CC -->|Yes| RFS
    CC -->|No| END[Do Nothing]
    RFS --> A
    RT --> A
    ER --> L

    classDef error fill:#f8d7da,stroke:#dc3545,stroke-width:2px;
    class ER error;`,
                                title: 'Transaction Engine',
                                description: 'Core invoicing engine with event handling and recalculation'
                            }
                        ]
                    },
                    {
                        layout: 'single',
                        diagrams: [
                            {
                                code: `flowchart TD
    subgraph Creation["Invoice Creation"]
        A[Transaction Created/Updated] --> B[Set Initial Status]
        B --> C{Customer Status}

        C -->|Inactive| D[No Invoice Created]
        C -->|Hold| E[Add to Held Invoice]
        C -->|Invoicing| F{Open Invoice Exists?}

        F -->|No| G[Create New Invoice]
        F -->|Yes| H{Invoice Date Passed?}

        H -->|Yes| G
        H -->|No| I[Add to Existing]

        E & I & G --> K[Update Invoice]
    end

    subgraph Processing["Invoice Processing"]
        K --> L{Needs Attention?}
        L -->|Yes| M[Await Review]
        L -->|No| N{Invoice Date Passed?}

        N -->|No| K
        N -->|Yes| O[Set Ready Status]

        M -->|Resolved| N

        O --> P{Send Delay Expired or Manual Send?}
        P -->|Yes| Q[Mark as Sent]
        P -->|No| O
    end

    subgraph Corrections["Correction Handling"]
        S[Change Required] --> T{Invoice Status}
        T -->|Sent| U[Credit/Rebill Process]
        T -->|Other| V[Direct Update]
        V --> A
        U --> W[Create Credit]
        W --> X[Create Rebill]
        X --> A
    end

    classDef process fill:#f5f5f5,stroke:#666
    classDef attention fill:#fff2cc,stroke:#d6b656
    classDef correction fill:#f8cecc,stroke:#b85450

    class M attention
    class U,W,X correction
    class A,G,I,K,O,Q process`,
                                title: 'Invoice Lifecycle',
                                description: 'Complete invoice flow from creation through corrections'
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'divider'
        },

        // 2. RATEBOOKS
        {
            type: 'heading2',
            content: '2. Ratebooks',
            id: 'ratebooks',
            description: 'Dimensional ratebooks with accessorial and surcharge engines for every customer scenario.'
        },
        {
            type: 'paragraph',
            content:
                'Every customer negotiated freight differently—by mile, by truck, by lane, plus layers of automatic and manual accessorials. We built a ratebook system that stores dimensional rules, triggers, and surcharges so the platform calculates freight without spreadsheets or tribal knowledge.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Problem', 'How We Solved It'],
                rows: [
                    [
                        'Base freight rates varied per counterparty and were stored in one-off spreadsheets.',
                        'Dimensional rate tables that combine counterparty, lane, product, and transport method without duplication.'
                    ],
                    [
                        'Order management and freight pricing were disconnected, so dispatch re-entered data.',
                        'Ratebooks share object IDs with orders—freight pricing inherits context directly from the order pipeline.'
                    ],
                    [
                        'Accessorial fees relied on binders and manual judgment.',
                        'Automatic and manual accessorial engines with condition builders, location trees, and effectivity windows.'
                    ],
                    [
                        'Fuel surcharges were recalculated manually each week.',
                        'Surcharge matrix with effective dating, approvals, and bulk import/export for finance.'
                    ]
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Ratebook Interfaces',
                caption: 'Tools that keep every customer contract codified and traceable.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                kind: 'video',
                                src: ratebookOverviewVideoAsset,
                                alt: 'Ratebook overview walkthrough',
                                caption: 'Rate type matrix shows mileage, flat, lane, and accessorial combinations with live calculations.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                kind: 'video',
                                src: ratebookDimensionsVideoAsset,
                                alt: 'Mileage band editor',
                                caption: 'Mileage bands copy from existing contracts and insert new bands without re-keying neighboring thresholds.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                kind: 'video',
                                src: ratebookConditionsVideoAsset,
                                alt: 'Automatic accessorial rule engine',
                                caption: 'Automatic accessorials use location filters, stop logic, and per-unit thresholds to trigger fees automatically.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    },
                    {
                        layout: 'double',
                        images: [
                            {
                                kind: 'video',
                                src: ratebookAccessorialVideoAsset,
                                alt: 'Account hold workflow',
                                caption: 'Putting a company on hold cascades to invoice processing and flags orders instantly.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            },
                            {
                                kind: 'video',
                                src: ratebookBulkUpdatesVideoAsset,
                                alt: 'Effective dating interface',
                                caption: 'Every change carries effective dates so finance sees when rates activate and how history evolves.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'divider'
        },

        // 3. LINE ITEMS + INVOICING
        {
            type: 'heading2',
            content: '3. Line Items & Invoicing',
            id: 'line-items',
            description: 'Live calculations, overrides with audit, and invoices that scale from single loads to portfolios.'
        },
        {
            type: 'paragraph',
            content:
                'Dispatch wanted to see freight before trucks rolled. Finance needed every calculation explained. We designed line-item intelligence that shows base freight, accessorials, surcharges, and manual overrides in one place—then built invoice views that stay readable whether they cover one order or an entire week of loads.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Problem', 'How We Solved It'],
                rows: [
                    [
                        'Pending freight was invisible until invoices posted.',
                        'Pending freight dashboards show cost and revenue exposure before dispatch approves an order.'
                    ],
                    [
                        'Line-item math lived in hidden spreadsheet formulas.',
                        'Line-item report surfaces every calculation component with filters, grouping, and exports.'
                    ],
                    [
                        'Overrides lacked audit trails and context.',
                        'Freight detail drawer logs who overrode what, when, why, and recalculates instantly.'
                    ],
                    [
                        'Invoices were either single-order or manual spreadsheet collages.',
                        'Dynamic invoice layouts switch automatically between single-order and multi-order presentation.'
                    ]
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Invoice & Line Item Intelligence',
                caption: 'Interfaces that make every freight dollar explainable.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                kind: 'video',
                                src: lineItemsIntelligenceVideoAsset,
                                alt: 'Line item intelligence walkthrough',
                                caption: 'Grouped line items with quick filters let teams flip views by order, status, or type without losing the story.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'divider'
        },

        // 4. CREDIT & REBILL
        {
            type: 'heading2',
            content: '4. Credit & Rebill',
            id: 'credit-rebill',
            description: 'Diffs, recalculations, and breadcrumbs that make invoice corrections painless.'
        },
        {
            type: 'paragraph',
            content:
                'Invoices freeze for compliance, but mistakes happen. We built a credit/rebill flow that recalculates freight, highlights diffs, and keeps breadcrumbs so customer conversations are grounded in facts instead of guesswork.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Problem', 'How We Solved It'],
                rows: [
                    [
                        'Correcting invoices meant voiding PDFs and manually rebuilding history.',
                        'Credit/rebill engine preserves the original invoice, generates deltas, and links new documents automatically.'
                    ],
                    [
                        'Teams could not explain what changed between original and corrected invoices.',
                        'Diff view shows before/after per line item, accessorial, and surcharge with net impact.'
                    ],
                    [
                        'Audit trails lived in email chains.',
                        'Breadcrumb timeline records who initiated adjustments, when they ran, and why.'
                    ]
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Credit/Rebill Guardrails',
                caption: 'Interfaces that keep corrections transparent.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                src: creditRebillUx,
                                alt: 'Credit/rebill UX showing git diff inspired ledger',
                                caption: 'Git-diff inspired credit/rebill flow flags which transactions were added, removed, or recalculated before finance hits send.'
                            }
                        ]
                    }
                ]
            }
        },

        // 5. LAUNCHPAD
        {
            type: 'heading2',
            content: '5. Launchpad',
            id: 'launchpad',
            description: 'Launchpad surfaces freight exceptions across 100+ customers before cash is at risk.'
        },
        {
            type: 'paragraph',
            content:
                'Freight revenue disappears when issues hide inside spreadsheets. Launchpad aggregates cost vs revenue variance, blocked invoices, manual overrides, and SLA breaches so teams know exactly where to focus.'
        },
        {
            type: 'comparisonTable',
            content: {
                headers: ['Problem', 'How We Solved It'],
                rows: [
                    [
                        'Carriers lacked a single view of freight health across customers.',
                        'Launchpad dashboards roll up cost, revenue, and margin trends with drill-ins per customer.'
                    ],
                    [
                        'Exceptions were discovered days later during reconciliation.',
                        'Real-time alerts flag missing data, unposted invoices, and high-variance loads before close.'
                    ],
                    [
                        'Analysts lost hours cross-referencing systems for answers.',
                        'Action cards deep-link into ratebooks, invoices, or credit/rebill flows with context already loaded.'
                    ]
                ]
            }
        },
        {
            type: 'gallery',
            content: {
                heading: 'Freight Launchpad',
                caption: 'Command center that keeps revenue flowing.',
                rows: [
                    {
                        layout: 'single',
                        images: [
                            {
                                kind: 'video',
                                src: launchpadVideo,
                                alt: 'Freight launchpad monitoring walkthrough',
                                caption: 'Launchpad highlights attention items, revenue exposure, and SLA breaches across the portfolio.',
                                autoPlay: true,
                                loop: true,
                                muted: true,
                                playsInline: true
                            }
                        ]
                    }
                ]
            }
        },
        {
            type: 'divider'
        },

        // CLOSING IMPACT
        // IMPACT STATEMENT REMOVED PER REQUEST
    ]
};

export default freightInvoicing;
